import { useEffect, useState } from 'react';
import { fetchJson, postJson } from '../api.js';

// ============================================================================
//  Users & Permissions admin page
// ============================================================================
//
//  Two tabs:
//    1. Allowlist  — who can sign in (POST/DELETE /api/users) + each user's
//                    group memberships (POST/DELETE /api/auth/group-members).
//    2. Groups     — list/edit/delete permission groups + per-tab permission
//                    matrix (GET/PUT/DELETE /api/auth/groups).
//
//  The tab list comes from TABS below; it must stay in sync with the routes
//  in App.jsx and the seed data in db/schema-003-permission-groups.sql.
// ============================================================================

async function putJson(path, body) {
  const res = await fetch(path, { method: 'PUT', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) });
  const text = await res.text();
  if (!res.ok) throw new Error(res.status + ': ' + text);
  try { return JSON.parse(text); } catch { return { ok: true }; }
}
async function deleteJson(path) {
  const res = await fetch(path, { method: 'DELETE', credentials: 'include' });
  const text = await res.text();
  if (!res.ok) throw new Error(res.status + ': ' + text);
  try { return JSON.parse(text); } catch { return { ok: true }; }
}

// Permission levels in increasing order
const LEVELS = ['none', 'read', 'write', 'admin'];
const LEVEL_COLOR = { none: '#888', read: '#0369a1', write: '#15803d', admin: '#991b1b' };
// FALLBACK ONLY (2026-10-06 overhaul): the live tab list now comes
// from /api/auth/groups (shared/tabs.js registry), so new tabs appear
// in this editor automatically. This constant is used only until that
// fetch resolves / if it fails.
// TABS is reassigned to the live registry served by /api/auth/groups
// (shared/tabs.js). FALLBACK_TABS covers first paint / fetch failure.
let TABS;
const FALLBACK_TABS = [
  { key: 'overview',       label: 'Overview' },
  { key: 'trucks',         label: 'Truck Volume' },
  { key: 'billing',        label: 'Revenue Recognition' },
  { key: 'balances',       label: 'Balances' },
  { key: 'revenue',        label: 'Billing (QBO)' },
  { key: 'vendor-spend',   label: 'Vendor Spend' },
  { key: 'review',         label: 'Review' },
  { key: 'reference',      label: 'Reference' },
  { key: 'outgoing-loads', label: 'Outgoing loads' },
  { key: 'daily-report',   label: 'Daily report' },
  { key: 'vehicle-log',    label: 'Vehicle Log' },
  { key: 'statements',     label: 'Statements' },
  { key: 'attention',      label: 'Attention' },
  { key: 'truck-analytics', label: 'Truck analytics' },
  { key: 'users',          label: 'Users' }
];
TABS = FALLBACK_TABS;

export default function Users() {
  const [view, setView] = useState('allowlist'); // 'allowlist' | 'groups'
  const [me, setMe] = useState(null);

  useEffect(() => { fetchJson('/api/whoami').then(setMe).catch(() => {}); }, []);

  if (me && !me.isAdmin) {
    return (
      <div className="chart-card">
        <h3>Access denied</h3>
        <div className="note">Only admin users can manage users + permissions.</div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-head">
        <h1>Users &amp; Permissions</h1>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', borderBottom: '1px solid #e5e7eb', marginBottom: '1rem' }}>
        <button onClick={() => setView('allowlist')}
          style={tabBtnStyle(view === 'allowlist')}>Allowlist</button>
        <button onClick={() => setView('groups')}
          style={tabBtnStyle(view === 'groups')}>Groups &amp; permissions</button>
      </div>
      {view === 'allowlist' ? <AllowlistPanel /> : <GroupsPanel />}
    </div>
  );
}

function tabBtnStyle(active) {
  return {
    background: 'transparent',
    border: 'none',
    borderBottom: active ? '2px solid var(--ecovac-green)' : '2px solid transparent',
    fontWeight: active ? 600 : 400,
    color: active ? '#000' : '#555',
    padding: '0.5rem 1rem',
    cursor: 'pointer',
    fontSize: '0.95rem'
  };
}

// ─── Allowlist panel ──────────────────────────────────────────────────────
function AllowlistPanel() {
  const [users, setUsers] = useState(null);
  const [groups, setGroups] = useState([]);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [busyMember, setBusyMember] = useState(null);

  async function load() {
    setError(null);
    try {
      const [u, g] = await Promise.all([
        fetchJson('/api/users'),
        fetchJson('/api/auth/groups').catch(() => ({ groups: [] }))
      ]);
      if (Array.isArray(g.tabs) && g.tabs.length) TABS = g.tabs;   // live registry
      setUsers(u.users || []);
      setGroups(g.groups || []);
    } catch (e) { setError(e.message); }
  }
  useEffect(() => { load(); }, []);

  async function handleAdd(e) {
    e.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    setAdding(true); setError(null);
    try {
      const r = await postJson('/api/users', { email });
      setUsers(r.users || users);
      setNewEmail('');
      if (r.added === false) setError('Already on the list.');
    } catch (e) { setError(e.message); }
    finally { setAdding(false); }
  }
  async function handleRemove(email) {
    if (!confirm('Remove ' + email + ' from the allowlist? They will lose access immediately.')) return;
    setRemoving(email); setError(null);
    try {
      const r = await deleteJson('/api/users?email=' + encodeURIComponent(email));
      setUsers(r.users || users.filter(u => u.email !== email));
    } catch (e) { setError(e.message); }
    finally { setRemoving(null); }
  }
  async function assignGroup(email, groupId) {
    if (!groupId) return;
    setBusyMember(email); setError(null);
    try {
      await postJson('/api/auth/group-members', { user_email: email, group_id: parseInt(groupId, 10) });
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusyMember(null); }
  }
  async function removeFromGroup(email, groupId) {
    setBusyMember(email); setError(null);
    try {
      await deleteJson('/api/auth/group-members?user_email=' + encodeURIComponent(email) + '&group_id=' + groupId);
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusyMember(null); }
  }

  if (!users && !error) return <div className="loading">Loading…</div>;

  return (
    <>
      <div className="note" style={{ marginBottom: '0.8rem' }}>
        Everyone on this list can sign in. Anyone else gets a 403, even if they have an EcoVac email.
        Assign groups to control which tabs each user sees. Changes take effect within ~60 sec (next page load).
      </div>

      <div className="chart-card" style={{ borderLeft: '4px solid var(--ecovac-green)' }}>
        <h3 style={{ margin: 0, marginBottom: '0.5rem' }}>Add a user</h3>
        <form onSubmit={handleAdd} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                 placeholder="someone@ecovac.ca" required
                 style={{ flex: 1, padding: '0.45rem 0.7rem', border: '1px solid #cbd5e1', borderRadius: 5, fontSize: '0.9rem' }} />
          <button type="submit" className="refresh-btn" disabled={adding || !newEmail.trim()}>
            {adding ? 'Adding…' : 'Add'}
          </button>
        </form>
        {error && <div className="note" style={{ color: 'var(--down)', marginTop: 6 }}>Error: {error}</div>}
      </div>

      <div className="chart-card" style={{ marginTop: '1rem' }}>
        <h3>Allowlist ({users ? users.length : 0})</h3>
        <table>
          <thead><tr>
            <th>Email</th>
            <th>Groups</th>
            <th style={{ width: 240 }}>Assign group</th>
            <th style={{ width: 100 }}>Actions</th>
          </tr></thead>
          <tbody>
            {(users || []).map(u => (
              <UserRow key={u.email} u={u} groups={groups}
                       busy={busyMember === u.email || removing === u.email}
                       onAssign={(gid) => assignGroup(u.email, gid)}
                       onRemoveGroup={(gid) => removeFromGroup(u.email, gid)}
                       onRemove={() => handleRemove(u.email)} />
            ))}
            {users && users.length === 0 && (
              <tr><td colSpan={4} style={{ color: '#888', padding: '1rem', textAlign: 'center' }}>No users yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

function UserRow({ u, groups, busy, onAssign, onRemoveGroup, onRemove }) {
  const [pendingGroupId, setPendingGroupId] = useState('');
  // u.groups is populated by /api/users if the endpoint includes it; otherwise
  // we look up by membership. To keep the user-CRUD endpoint untouched, we
  // fetch memberships per-row lazily via /api/auth/groups[group.member_count].
  // Simpler: render groups assigned to this user by membership lookup once
  // groups are loaded. The /api/users endpoint returns memberships in
  // `u.groups` if it supports it; otherwise we fall back to '—'.
  const userGroups = u.groups || [];
  const remaining = groups.filter(g => !userGroups.some(ug => ug.id === g.id || ug.name === g.name));

  return (
    <tr>
      <td>{u.email}</td>
      <td>
        {userGroups.length === 0
          ? <span style={{ color: '#888' }}>—</span>
          : userGroups.map(g => (
            <span key={g.id || g.name} style={{
              display: 'inline-block', background: '#f0f9ff', color: '#075985',
              border: '1px solid #bae6fd', padding: '2px 8px', borderRadius: 999,
              fontSize: '0.8em', marginRight: 4, marginBottom: 2
            }}>
              {g.name}{' '}
              <button onClick={() => onRemoveGroup(g.id)} disabled={busy}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#991b1b', padding: 0, fontSize: '1em' }}
                title={'Remove ' + u.email + ' from ' + g.name}>×</button>
            </span>
          ))}
      </td>
      <td>
        <select value={pendingGroupId} onChange={(e) => setPendingGroupId(e.target.value)}
                disabled={busy || remaining.length === 0}
                style={{ padding: '0.3rem', fontSize: '0.85em' }}>
          <option value="">— pick group —</option>
          {remaining.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        <button className="link" disabled={busy || !pendingGroupId}
          onClick={() => { onAssign(pendingGroupId); setPendingGroupId(''); }}
          style={{ marginLeft: 6 }}>Add</button>
      </td>
      <td>
        <button onClick={onRemove} disabled={busy}
          style={{ background: 'transparent', border: '1px solid #991b1b', color: '#991b1b',
                   padding: '0.25rem 0.6rem', borderRadius: 4, cursor: 'pointer', fontSize: '0.85em' }}>
          {busy ? '…' : 'Remove'}
        </button>
      </td>
    </tr>
  );
}

// ─── Groups panel ─────────────────────────────────────────────────────────
function GroupsPanel() {
  const [groups, setGroups] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // group object being edited (null = closed)
  const [busy, setBusy] = useState(false);

  async function load() {
    setError(null);
    try {
      const r = await fetchJson('/api/auth/groups');
      if (Array.isArray(r.tabs) && r.tabs.length) TABS = r.tabs;   // live registry from shared/tabs.js
      setGroups(r.groups || []);
    } catch (e) { setError(e.message); }
  }
  useEffect(() => { load(); }, []);

  async function handleSave(g) {
    setBusy(true); setError(null);
    try {
      await putJson('/api/auth/groups', g);
      setEditing(null);
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }
  async function handleDelete(g) {
    if (!confirm('Delete group "' + g.name + '"? All memberships will be removed and members will fall back to the default group.')) return;
    setBusy(true); setError(null);
    try {
      await deleteJson('/api/auth/groups/' + g.id);
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  if (!groups && !error) return <div className="loading">Loading…</div>;

  return (
    <>
      <div className="note" style={{ marginBottom: '0.8rem' }}>
        Each group is a JSON map of tab → permission level (<code>none</code> · <code>read</code> · <code>write</code> · <code>admin</code>).
        A user inherits the highest level across all their groups. Mark one group as <strong>default</strong> to apply to allowlisted users with no explicit assignment.
      </div>

      <div className="chart-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Groups ({groups ? groups.length : 0})</h3>
          <button className="refresh-btn" onClick={() => setEditing(emptyGroup())}>+ Create group</button>
        </div>
        {error && <div className="note" style={{ color: 'var(--down)', marginTop: 6 }}>Error: {error}</div>}
        <table style={{ marginTop: '0.8rem' }}>
          <thead><tr>
            <th>Name</th>
            <th>Description</th>
            <th>Members</th>
            <th>Flags</th>
            <th>Permissions preview</th>
            <th style={{ width: 140 }}>Actions</th>
          </tr></thead>
          <tbody>
            {(groups || []).map(g => (
              <tr key={g.id}>
                <td style={{ fontWeight: 600 }}>{g.name}</td>
                <td style={{ color: '#555', fontSize: '0.9em' }}>{g.description}</td>
                <td>{g.member_count || 0}</td>
                <td>
                  {g.is_admin   && <Badge color="#991b1b" label="admin" />}
                  {g.is_default && <Badge color="#0369a1" label="default" />}
                </td>
                <td><PermsPreview perms={g.tab_permissions || {}} /></td>
                <td>
                  <button className="link" onClick={() => setEditing(g)} disabled={busy}>Edit</button>
                  {!g.is_default && (
                    <button onClick={() => handleDelete(g)} disabled={busy}
                      style={{ marginLeft: 8, background: 'transparent', border: 'none', color: '#991b1b', cursor: 'pointer' }}>
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <GroupEditorModal group={editing} onCancel={() => setEditing(null)} onSave={handleSave} busy={busy} />
      )}
    </>
  );
}

function emptyGroup() {
  const tp = {}; for (const t of TABS) tp[t.key] = 'none';
  return { name: '', description: '', is_default: false, is_admin: false, tab_permissions: tp };
}

function Badge({ color, label }) {
  return <span style={{
    display: 'inline-block', background: color + '22', color: color,
    border: '1px solid ' + color + '44', padding: '1px 8px', borderRadius: 999,
    fontSize: '0.75em', marginRight: 4, fontWeight: 600
  }}>{label}</span>;
}
function PermsPreview({ perms }) {
  // Show only tabs with non-none permission
  const items = TABS.filter(t => perms[t.key] && perms[t.key] !== 'none');
  if (items.length === 0) return <span style={{ color: '#888' }}>—</span>;
  if (items.length === TABS.length && items.every(t => perms[t.key] === 'admin')) {
    return <Badge color={LEVEL_COLOR.admin} label="ALL · admin" />;
  }
  return (
    <span style={{ fontSize: '0.8em' }}>
      {items.slice(0, 6).map(t => (
        <span key={t.key} title={t.label + ': ' + perms[t.key]} style={{
          display: 'inline-block', background: LEVEL_COLOR[perms[t.key]] + '22',
          color: LEVEL_COLOR[perms[t.key]], padding: '1px 6px', borderRadius: 3,
          marginRight: 3, marginBottom: 2
        }}>{t.label}: {perms[t.key]}</span>
      ))}
      {items.length > 6 && <span style={{ color: '#888' }}>+{items.length - 6} more</span>}
    </span>
  );
}

// ─── Group editor modal ───────────────────────────────────────────────────
function GroupEditorModal({ group, onCancel, onSave, busy }) {
  const [form, setForm] = useState(() => ({
    id: group.id, name: group.name || '', description: group.description || '',
    is_default: !!group.is_default, is_admin: !!group.is_admin,
    // MERGE with the group's existing map: the old code rebuilt it from
    // the (stale) tab list, silently STRIPPING unknown tabs on save.
    tab_permissions: Object.assign({}, group.tab_permissions || {}, ...TABS.map(t => ({ [t.key]: group.tab_permissions?.[t.key] || 'none' })))
  }));
  function setPerm(tab, level) {
    setForm(f => ({ ...f, tab_permissions: { ...f.tab_permissions, [tab]: level } }));
  }
  function setAllPerms(level) {
    setForm(f => ({ ...f, tab_permissions: Object.assign({}, f.tab_permissions, ...TABS.map(t => ({ [t.key]: level }))) }));
  }

  return (
    <div className="modal-backdrop" onClick={() => !busy && onCancel()}>
      <div className="modal-card" onClick={e => e.stopPropagation()}
           style={{ minWidth: 640, maxWidth: '92vw', maxHeight: '90vh', overflow: 'auto' }}>
        <h3 style={{ marginTop: 0 }}>{group.id ? 'Edit group: ' + group.name : 'Create group'}</h3>

        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0.5rem 0.8rem', alignItems: 'center' }}>
          <label>Name</label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                 placeholder="e.g. drivers, accounting, viewers" />
          <label>Description</label>
          <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                 placeholder="What this group is for" />
          <label></label>
          <div>
            <label style={{ marginRight: '1rem' }}>
              <input type="checkbox" checked={form.is_admin} onChange={(e) => setForm({ ...form, is_admin: e.target.checked })} />
              {' '}Admin (bypasses every permission check)
            </label>
            <label>
              <input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} />
              {' '}Default group (applies to anyone with no group assigned)
            </label>
          </div>
        </div>

        <div style={{ marginTop: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h4 style={{ margin: 0 }}>Tab permissions</h4>
            <div className="small">
              Set all to:{' '}
              {LEVELS.map(L => (
                <button key={L} className="link" onClick={() => setAllPerms(L)}
                        style={{ marginLeft: 4 }}>{L}</button>
              ))}
            </div>
          </div>
          <table style={{ marginTop: '0.5rem', width: '100%' }}>
            <thead><tr>
              <th style={{ textAlign: 'left' }}>Tab</th>
              {LEVELS.map(L => <th key={L} style={{ width: 70, textAlign: 'center', color: LEVEL_COLOR[L] }}>{L}</th>)}
            </tr></thead>
            <tbody>
              {TABS.map(t => (
                <tr key={t.key}>
                  <td>{t.label} <span className="small" style={{ color: '#888' }}>({t.key})</span></td>
                  {LEVELS.map(L => (
                    <td key={L} style={{ textAlign: 'center' }}>
                      <input type="radio" name={'perm-' + t.key} checked={form.tab_permissions[t.key] === L}
                             onChange={() => setPerm(t.key, L)} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: '1.2rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button className="refresh-btn" onClick={onCancel} disabled={busy}
                  style={{ background: '#888' }}>Cancel</button>
          <button className="refresh-btn" onClick={() => onSave(form)} disabled={busy || !form.name.trim()}>
            {busy ? 'Saving…' : (group.id ? 'Save changes' : 'Create group')}
          </button>
        </div>
      </div>
    </div>
  );
}
